/**
 * Página principal del módulo de participantes.
 *
 * Esta página proporciona la interfaz principal de pestañas para intercambiar entre la gestion de
 * participantes(Editar, cargar, crear, exportar y filtrar ) y gestionar archivos de carga
 * masiva de participantes, incluyendo el layout, navegación y título de la página.
 *
 * @author Shara Milena Mosquera Goez <shara.mosquera@iudigital.edu.co>
 * @date [Fecha de creación: 2024-09-05]
 * @updated [Fecha de última modificación: 2025-08-12]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import { Box, Card, Grid, Tab, Tabs } from "@mui/material";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import ParticipantsController from "./Controllers/ParticipantsController";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HistoryIcon from "@mui/icons-material/History";
import ParticipantFilesController from "layouts/participant_files/controllers/ParticipantFilesController";
import { useMaterialUIController } from "context";
import colors from "assets/theme/base/colors";

export default function Participant() {
  const location = useLocation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { white } = colors;

  // Función para extraer el nombre de la actividad del estado
  const getActivityName = () => {
    const state = location.state;

    // Si activityTitle es un objeto, extraer su propiedad activityName
    if (state?.activityTitle && typeof state.activityTitle === 'object') {
      return state.activityTitle.activityName || "";
    }

    // Si activityTitle es un string, usarlo directamente
    if (state?.activityTitle && typeof state.activityTitle === 'string') {
      return state.activityTitle;
    }

    // Si activityName existe, usarlo
    if (state?.activityName && typeof state.activityName === 'string') {
      return state.activityName;
    }

    // Fallback a sessionStorage
    return sessionStorage.getItem("activityTitle") || "";
  };

  const activityName = getActivityName();

  const [nameSource, setNameSource] = useState("");
  const [valueTab, setValueTab] = useState(0); // Estado para controlar las pestañas

  // Manejar cambio de pestañas
  const handleChangeTab = (event, newValue) => {
    setValueTab(newValue);
  };

  useEffect(() => {
    // Guardar activityTitle en sessionStorage si existe
    const titleToSave = getActivityName();
    if (titleToSave) {
      sessionStorage.setItem("activityTitle", titleToSave);
    }

    // Recuperar datos de sessionStorage
    const storedNameSource = sessionStorage.getItem("nameSource");
    if (storedNameSource) setNameSource(storedNameSource);
  }, [location.state]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info">
                <MDTypography variant="h3" color="white">
                  {`Listado de participantes para la actividad: ${activityName}`}
                </MDTypography>
              </MDBox>
              {/* Pestañas */}
              <Box
                sx={{
                  width: { xs: "92%", sm: "35%" }, // Ancho completo en pantallas pequeñas, 35% en pantallas grandes
                  mt: 3,
                  position: "relative",
                  left: 15,
                  right: 1,
                }}>
                <Tabs
                  value={valueTab}
                  onChange={handleChangeTab}
                  aria-label="simple tabs example"
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" }, // Cambiar dirección en pantallas pequeñas
                    "& .MuiTab-root": {
                      textTransform: "none",
                      borderBottom: "none", // Eliminar borde inferior
                      borderRadius: "4px 4px 0 0", // Bordes redondeados en la parte superior
                      marginRight: { sm: "4px" }, // Espacio entre pestañas en pantallas grandes
                      marginBottom: { xs: "4px", sm: "0" }, // Espacio entre pestañas en pantallas pequeñas
                      backgroundColor: darkMode ? "#2d3748" : "#f0f0f0", // Fondo gris claro/oscuro
                      color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.87)", // Color de texto
                      padding: "5px 4px", // Ajustar padding
                      flex: 1, // Ocupar todo el espacio disponible
                      "&:hover": {
                        backgroundColor: darkMode ? "#3a4556" : "#e0e0e0", // Fondo al hover
                      },
                      "&.Mui-selected": {
                        border: "2px solid #3e98ef", // Borde azul
                        backgroundColor: darkMode ? "#1a2035" : "#ffffff", // Fondo para pestañas seleccionadas
                        color: darkMode ? white.main : "rgba(0, 0, 0, 0.87)", // Color de texto seleccionado
                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)", // Sombra para pestañas seleccionadas
                        borderBottom: "none", // Eliminar borde inferior de la pestaña seleccionada
                      },
                      minWidth: { sm: 200 }, // Ancho mínimo para cada pestaña en pantallas grandes
                      "@media (max-width: 600px)": {
                        minWidth: "100%", // Ancho completo para pantallas más pequeñas
                      },
                    },
                    "& .MuiTabs-scrollButtons": {
                      "&.Mui-disabled": { opacity: 0.3 },
                    },
                  }}>
                  <Tab
                    icon={<ListAltIcon fontSize="small" />}
                    value={0}
                    label="Listado de participantes"
                    wrapped
                  />
                  <Tab
                    icon={<HistoryIcon fontSize="small" />}
                    value={1}
                    label="Historial de fuentes de datos"
                  />
                </Tabs>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: { xs: "89%", sm: "97%" }, // 94% en pantallas pequeñas, 97% en pantallas grandes
                  bottom: 0,
                  left: 19,
                  right: 1,
                  height: "2px",
                  backgroundColor: "#3e98ef", // Color de la línea adicional
                  top: "-4px",
                }}
              />
              {valueTab === 0 && (
                <MDBox pt={3}>
                  <ParticipantsController />
                </MDBox>
              )}
              {valueTab === 1 && (
                <MDBox pt={3}>
                  <ParticipantFilesController activityName={activityName} />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
